import { supabase } from '@/integrations/supabase/client'; 
import { STORAGE_KEYS } from '../constants/storage-keys';
import { saveData } from './entry-save-service';
import { loadData } from '../utils/local-storage';
import { toast } from 'sonner';

// Define UserSettingsRow type for database interactions
export interface UserSettingsRow {
  id: string;
  user_id: string;
  calorie_goal: number;
  walking_goal: number;
  fasting_goal: number;
  sleep_goal: number;
  water_goal: number;
  water_unit: 'ml' | 'oz';
  created_at: string;
  updated_at: string;
}

// Define the UserSettings type that's used in the app
export interface UserSettings {
  calorieGoal: number;
  walkingGoal: number;
  fastingGoal: number;
  sleepGoal: number;
  waterGoal: number;
  waterUnit: 'ml' | 'oz';
  profileId?: string;
}

// Default settings if nothing is found
export const DEFAULT_SETTINGS: UserSettings = {
  calorieGoal: 2000,
  walkingGoal: 30,
  fastingGoal: 16 * 60 * 60 * 1000, // 16 hours in ms
  sleepGoal: 8.0,
  waterGoal: 2000,
  waterUnit: 'ml'
};

// Add loadSettingsFromDatabase function
export const loadSettingsFromDatabase = async (): Promise<UserSettings> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    
    if (!userId) {
      console.log('User not authenticated, using default settings');
      return DEFAULT_SETTINGS;
    }
    
    const settings = await getUserSettingsFromDB(userId);
    return settings;
  } catch (error) {
    console.error('Error loading settings from database:', error);
    return DEFAULT_SETTINGS;
  }
};

// Add saveSettingsToDatabase function
export const saveSettingsToDatabase = async (settings: UserSettings): Promise<boolean> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    
    if (!userId) {
      console.log('User not authenticated, skipping database save');
      return false;
    }
    
    return await saveUserSettings(settings, userId);
  } catch (error) {
    console.error('Error saving settings to database:', error);
    return false;
  }
};

// Get user settings with proper error handling and fallback
export const getUserSettingsFromDB = async (userId: string): Promise<UserSettings> => {
  try {
    if (!userId) {
      console.warn('getUserSettings called without userId');
      return DEFAULT_SETTINGS;
    }

    // Use direct table access instead of RPC
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error getting user settings:', error);
      throw error;
    }
    
    if (!data) {
      // If no settings found, create default settings
      const newSettings = await createDefaultUserSettings(userId);
      return newSettings || DEFAULT_SETTINGS;
    }
    
    const userSettings = data as UserSettingsRow;
    
    return {
      calorieGoal: userSettings.calorie_goal || DEFAULT_SETTINGS.calorieGoal,
      walkingGoal: userSettings.walking_goal || DEFAULT_SETTINGS.walkingGoal,
      fastingGoal: userSettings.fasting_goal || DEFAULT_SETTINGS.fastingGoal,
      sleepGoal: userSettings.sleep_goal || DEFAULT_SETTINGS.sleepGoal,
      waterGoal: userSettings.water_goal || DEFAULT_SETTINGS.waterGoal,
      waterUnit: userSettings.water_unit as 'ml' | 'oz' || DEFAULT_SETTINGS.waterUnit,
      profileId: userSettings.id
    };
  } catch (error) {
    console.error('Error fetching user settings:', error);
    toast.error('Could not load your settings');
    return DEFAULT_SETTINGS;
  }
};

// Create default user settings when a user first accesses them
export const createDefaultUserSettings = async (userId: string): Promise<UserSettings | null> => {
  try {
    if (!userId) {
      console.warn('createDefaultUserSettings called without userId');
      return null;
    }
    
    // Save to local cache first
    const localSettings = { 
      ...DEFAULT_SETTINGS,
      profileId: userId
    };
    
    // Try to save to database
    const { data, error } = await supabase
      .from('user_settings')
      .insert({
        user_id: userId,
        calorie_goal: DEFAULT_SETTINGS.calorieGoal,
        walking_goal: DEFAULT_SETTINGS.walkingGoal,
        fasting_goal: DEFAULT_SETTINGS.fastingGoal,
        sleep_goal: DEFAULT_SETTINGS.sleepGoal,
        water_goal: DEFAULT_SETTINGS.waterGoal,
        water_unit: DEFAULT_SETTINGS.waterUnit
      })
      .select()
      .single();
    
    if (error) {
      // If already exists, just return local settings
      if (error.code === '23505') {
        console.log('Settings already exist for user');
        return localSettings;
      }
      console.error('Error creating user settings:', error);
      return localSettings;
    }
    
    if (data) {
      const settings = data as UserSettingsRow;
      return {
        calorieGoal: settings.calorie_goal,
        walkingGoal: settings.walking_goal,
        fastingGoal: settings.fasting_goal,
        sleepGoal: settings.sleep_goal,
        waterGoal: settings.water_goal,
        waterUnit: settings.water_unit as 'ml' | 'oz',
        profileId: settings.id
      };
    }
    
    return localSettings;
  } catch (error) {
    console.error('Error creating default user settings:', error);
    return DEFAULT_SETTINGS;
  }
};

// Save user settings
export const saveUserSettings = async (settings: UserSettings, userId: string): Promise<boolean> => {
  try {
    if (!userId) {
      console.warn('saveUserSettings called without userId');
      return false;
    }
    
    // Save to database using upsert
    const { error } = await supabase
      .from('user_settings')
      .upsert({
        user_id: userId,
        calorie_goal: settings.calorieGoal,
        walking_goal: settings.walkingGoal,
        fasting_goal: settings.fastingGoal,
        sleep_goal: settings.sleepGoal,
        water_goal: settings.waterGoal,
        water_unit: settings.waterUnit
      }, {
        onConflict: 'user_id'
      });
    
    if (error) {
      console.error('Error saving user settings:', error);
      throw error;
    }
    
    toast.success('Settings saved');
    return true;
  } catch (error) {
    console.error('Error saving settings:', error);
    toast.error('Failed to save settings');
    return false;
  }
};
